import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import { Info } from '@mui/icons-material';

import usePharmacyStats from 'app/hooks/usePharmacyStats';
import { fetchUserPharmacies, changeSelectedPharmacy } from './store/pharmacySelectorSlice';

function PharmacySelector({ disabled = false, hidden = false }) {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { pharmacies, selectedPharmacyId } = useSelector((state) => state.pharmacySelector);

  usePharmacyStats();

  useEffect(() => {
    (async () => {
      if (!user._id || pharmacies !== undefined) return;

      await dispatch(fetchUserPharmacies(user._id));
      dispatch(changeSelectedPharmacy());
    })();
  }, [dispatch, user._id, pharmacies]);

  function handlePharmacyChange(event, newValue) {
    if (newValue) {
      dispatch(changeSelectedPharmacy(newValue._id));
    }
  }

  const selectedPharmacy = useMemo(() => {
    if (!selectedPharmacyId || !pharmacies?.length) return null;
    return pharmacies.find((pharmacy) => pharmacy._id === selectedPharmacyId) || null;
  }, [selectedPharmacyId, pharmacies]);

  function filterOptions(options, { inputValue }) {
    const lowerInput = inputValue.toLowerCase();
    return options.filter((option) => option.pharmacy_name.toLowerCase().startsWith(lowerInput));
  }

  return (
    <div className={`flex items-center ${hidden ? 'hidden' : ''}`}>
      <Autocomplete
        value={selectedPharmacy}
        onChange={handlePharmacyChange}
        options={pharmacies || []}
        getOptionLabel={(option) => option.pharmacy_name}
        filterOptions={filterOptions}
        size="small"
        disabled={disabled}
        className="min-w-[320px]"
        sx={autocompleteStyles}
        renderInput={(params) => (
          <TextField
            {...params}
            className="font-600 text-14 text-dark bg-white rounded-lg"
            sx={textFieldStyles}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <span className="mr-6">{option.pharmacy_name}</span>
            {option.status === 'inactive' && <span>(Inactive)</span>}
            {option.status === 'archived' && <span>(Archived)</span>}
          </li>
        )}
      />
      <Tooltip
        title={
          disabled
            ? 'Pharmacy cannot be changed for this view'
            : 'If you belong to multiple pharmacies, you can switch between your pharmacies from this drop-down list'
        }
        placement="right"
      >
        <div>
          <Info className="text-white ml-10" />
        </div>
      </Tooltip>
    </div>
  );
}

const autocompleteStyles = {
  '& .MuiOutlinedInput-root': {
    boxShadow: 'none',
    '& fieldset': {
      borderWidth: 0,
    },
    '&:hover fieldset': {
      borderWidth: 0,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 0,
    },
  },
};

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    boxShadow: 'none',
    '& fieldset': {
      borderWidth: 0,
    },
    '&:hover fieldset': {
      borderWidth: 0,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 0,
    },
  },
};

export default PharmacySelector;
